/**
 * Shared error message extraction utilities for API error responses.
 *
 * FastAPI returns `detail` in two different shapes:
 *   - string  — for HTTPException (e.g. {"detail": "Not found"})
 *   - array   — for Pydantic RequestValidationError (422)
 *               e.g. {"detail": [{"loc": ["body","field"], "msg": "...", "type": "..."}]}
 *
 * Naively passing an array to `new Error()` produces "[object Object],[object Object]"
 * which is confusing and unhelpful to users. Always use `extractApiErrorMessage` instead
 * of reading `.detail` directly.
 */

/**
 * Extract a human-readable error message from a FastAPI error response body.
 *
 * @param data       - The parsed response body (e.g. `error.response.data`)
 * @param statusCode - The HTTP status code (used as a fallback label)
 * @returns          A user-readable string, never "[object Object]"
 */
export function extractApiErrorMessage(data: unknown, statusCode: number): string {
  const response = data as { detail?: unknown } | null | undefined;
  const detail = response?.detail;

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    // Pydantic validation errors: [{loc: [...], msg: "...", type: "..."}, ...]
    const messages = detail
      .map((err: { msg?: string; loc?: string[] }) => {
        const field = err.loc?.slice(-1)[0] ?? 'field';
        return err.msg ? `${field}: ${err.msg}` : null;
      })
      .filter((m): m is string => m !== null);

    if (messages.length > 0) {
      return messages.join('; ');
    }
  }

  return `Request failed with status ${statusCode}`;
}

/**
 * Extract a human-readable error message from an Axios error (or any error with a
 * `response.data` property containing a FastAPI body).
 *
 * This variant is useful when you have the full Axios error object rather than just
 * the response body.
 *
 * @param err      - Unknown error (typically caught with `catch (err: unknown)`)
 * @param fallback - Fallback message when no structured detail is available
 * @returns        A user-readable string, never "[object Object]"
 */
export function extractAxiosErrorMessage(err: unknown, fallback: string): string {
  const axiosError = err as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };

  if (axiosError?.response?.data !== undefined) {
    const statusCode = axiosError.response?.status ?? 0;
    const extracted = extractApiErrorMessage(axiosError.response.data, statusCode);
    // Only use the extracted message if it's not just the generic status fallback or
    // the data had a non-empty detail field — otherwise prefer .message or fallback
    const data = axiosError.response.data as { detail?: unknown };
    if (data?.detail !== undefined && data?.detail !== null) {
      return extracted;
    }
  }

  return axiosError?.message || fallback;
}
